'use client'

import React from 'react'
import {
  Search,
  Radar,
  Users,
  ArrowDown,
  ArrowRight,
  RotateCcw,
  Clock,
  Zap,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Shield,
  Eye,
  TrendingUp,
} from 'lucide-react'

/**
 * AI Visibility Stack Users Diagrams
 * Visual components for the 50 Users blog post (AI Visibility Stack series, Part 1)
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * User Journey Funnel — Shows the product-led growth path from free tools to waitlist
 */
export function UserJourneyFunnelDiagram() {
  const stages = [
    {
      label: 'Organic Search Discovery',
      sublabel: 'User finds a free tool via Google',
      icon: Search,
      color: '#008dcc',
      metric: 'Zero paid acquisition',
      width: '100%',
    },
    {
      label: 'Free Tool Reveals a Problem',
      sublabel: 'AI bots blocked, no citations, wrong brand info',
      icon: AlertTriangle,
      color: '#FDC304',
      metric: '9 tools, no signup required',
      width: '85%',
    },
    {
      label: 'Radar Token Request',
      sublabel: 'User wants the full 13-tool audit',
      icon: Radar,
      color: '#F90B8A',
      metric: '50 token requests',
      width: '65%',
    },
    {
      label: 'Full Audit: All 13 Tools',
      sublabel: 'Cross-tool insights surface hidden conflicts',
      icon: Zap,
      color: '#8b5cf6',
      metric: '10+ min avg session',
      width: '55%',
    },
    {
      label: 'Return Visit to Measure Impact',
      sublabel: 'Users implement fixes, re-audit to compare scores',
      icon: RotateCcw,
      color: '#008dcc',
      metric: 'Repeat engagement',
      width: '40%',
    },
    {
      label: 'Expired Token → Waitlist',
      sublabel: 'Users request continued access instead of leaving',
      icon: Users,
      color: '#22c55e',
      metric: 'Demand exceeds access',
      width: '30%',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE USER JOURNEY: FREE TOOL TO WAITLIST
        </h3>
        <p className='text-sm text-muted-foreground'>
          How 50 users moved through the product-led growth funnel with zero
          sales involvement
        </p>
      </div>

      <div className='flex flex-col items-center gap-2'>
        {stages.map((stage, i) => (
          <React.Fragment key={stage.label}>
            <div
              className='w-full transition-all'
              style={{ maxWidth: stage.width }}
            >
              <div className='p-4 rounded-xl bg-background border border-border/60'>
                <div className='flex items-start gap-3'>
                  <div
                    className='w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0'
                    style={{ backgroundColor: `${stage.color}15` }}
                  >
                    <stage.icon
                      className='w-5 h-5'
                      style={{ color: stage.color }}
                    />
                  </div>
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center justify-between gap-2 flex-wrap'>
                      <p className='text-sm font-semibold text-foreground leading-tight'>
                        {stage.label}
                      </p>
                      <span
                        className='text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0'
                        style={{
                          backgroundColor: `${stage.color}15`,
                          color: stage.color,
                        }}
                      >
                        {stage.metric}
                      </span>
                    </div>
                    <p className='text-xs text-muted-foreground mt-0.5'>
                      {stage.sublabel}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {i < stages.length - 1 && (
              <div className='flex justify-center'>
                <ArrowDown className='w-4 h-4 text-muted-foreground' />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className='mt-6 p-3 rounded-lg bg-[#008dcc]/5 border border-[#008dcc]/20 text-center'>
        <p className='text-xs text-[#008dcc] font-medium'>
          <Clock className='w-3 h-3 inline mr-1' />
          Entire funnel is self-serve. No sales calls, no email sequences, no
          gated content.
        </p>
      </div>
    </div>
  )
}

/**
 * Cross-Tool Conflict Diagram — Shows how two tools contradict and Radar resolves
 */
export function CrossToolConflictsDiagram() {
  const conflicts = [
    {
      toolA: {
        name: 'Crawl Check',
        icon: Eye,
        result: 'GPTBot can access your site',
        status: 'pass' as const,
      },
      toolB: {
        name: 'Citation Tracker',
        icon: Search,
        result: 'ChatGPT does not mention your brand',
        status: 'fail' as const,
      },
      insight:
        'Access is not the problem. Your pages lack entity markup and structured data for ChatGPT to cite.',
      action: 'Add JSON-LD schema and llms.txt',
      color: '#008dcc',
    },
    {
      toolA: {
        name: 'robots.txt Analyzer',
        icon: Shield,
        result: 'All AI search bots allowed',
        status: 'pass' as const,
      },
      toolB: {
        name: 'AI Readiness Score',
        icon: TrendingUp,
        result: 'Score: 35/100',
        status: 'fail' as const,
      },
      insight:
        'Allowing bots is necessary but not sufficient. Missing llms.txt, schema, and speakable markup.',
      action: 'Implement structured data stack',
      color: '#F90B8A',
    },
    {
      toolA: {
        name: 'Share of Voice',
        icon: TrendingUp,
        result: 'You rank 3rd in your category',
        status: 'pass' as const,
      },
      toolB: {
        name: 'Source Influence',
        icon: Search,
        result: 'Competitor blog is the primary AI source',
        status: 'fail' as const,
      },
      insight:
        'Your position depends on content you do not control. One competitor piece is shaping AI answers.',
      action: 'Create authoritative content on that topic',
      color: '#8b5cf6',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          CROSS-TOOL CONFLICTS RADAR DETECTS
        </h3>
        <p className='text-sm text-muted-foreground'>
          Two tools show passing results. The conflict between them reveals the
          real problem.
        </p>
      </div>

      <div className='flex flex-col gap-5'>
        {conflicts.map((conflict, i) => (
          <div
            key={i}
            className='rounded-xl bg-background border border-border/60 overflow-hidden'
          >
            {/* Two tools side by side */}
            <div className='grid grid-cols-2 divide-x divide-border/60'>
              <div className='p-3'>
                <div className='flex items-center gap-2 mb-2'>
                  <div className='w-6 h-6 rounded flex items-center justify-center bg-[#22c55e]/10'>
                    <CheckCircle className='w-3.5 h-3.5 text-[#22c55e]' />
                  </div>
                  <span className='text-xs font-semibold text-foreground'>
                    {conflict.toolA.name}
                  </span>
                </div>
                <p className='text-xs text-foreground/80'>
                  {conflict.toolA.result}
                </p>
              </div>
              <div className='p-3'>
                <div className='flex items-center gap-2 mb-2'>
                  <div className='w-6 h-6 rounded flex items-center justify-center bg-[#ef4444]/10'>
                    <XCircle className='w-3.5 h-3.5 text-[#ef4444]' />
                  </div>
                  <span className='text-xs font-semibold text-foreground'>
                    {conflict.toolB.name}
                  </span>
                </div>
                <p className='text-xs text-foreground/80'>
                  {conflict.toolB.result}
                </p>
              </div>
            </div>

            {/* Radar insight */}
            <div
              className='px-3 py-2.5 border-t border-border/60'
              style={{ backgroundColor: `${conflict.color}08` }}
            >
              <div className='flex items-start gap-2'>
                <div
                  className='w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5'
                  style={{ backgroundColor: `${conflict.color}15` }}
                >
                  <Radar
                    className='w-3 h-3'
                    style={{ color: conflict.color }}
                  />
                </div>
                <div>
                  <p className='text-xs font-semibold text-foreground'>
                    Radar Insight
                  </p>
                  <p className='text-xs text-foreground/80 mt-0.5'>
                    {conflict.insight}
                  </p>
                  <p className='text-xs mt-1' style={{ color: conflict.color }}>
                    <ArrowRight className='w-3 h-3 inline mr-1' />
                    {conflict.action}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className='mt-5 p-3 rounded-lg bg-[#F90B8A]/5 border border-[#F90B8A]/20 text-center'>
        <p className='text-xs text-[#F90B8A] font-medium'>
          These conflicts exist between tool outputs, not within them. No single
          tool can detect them.
        </p>
      </div>
    </div>
  )
}
